import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";
import Typography from 'material-ui/Typography';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compressSelectionToPaths } from '../../../../../utils/treeUtils';
import { Set } from 'immutable';

const styles = () => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    flexDirection: 'unset',
    justifyContent: 'flex-start',
    borderBottom: '1px solid #D8DBDF',
    marginBottom: '18px',
  },

  title: {
    boxSizing: 'border-box',
    paddingBottom: '18px',
    color: '#181819',
    fontSize: '13px',
    fontWeight: 'bold',
  },

  boxWrap: {
    display: 'flex',
    position: 'relative',
  },

  box: {
    width: '7px',
    height:'7px',
    position: 'absolute',
    border: '1px solid #181819',
  },

  inline: {
    display: 'inline-flex',
    alignItems: 'center',
    width: '100%',
  },

  corpLogo: {
    height: '60px',
    width: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    marginRight: '20px',
  },

  logoImg: {
    maxWidth: '60px',
    maxHeight: '60px',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    backgroundColor: '#F2F6FF',
    padding: '10px',
    borderRadius: '5px',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '10px',
  },
  industryLabel: {
    display: 'inline-block',
    fontSize: '10px',
    padding: '2px 0',
    borderRadius: '3px',
    marginRight: '8px',
    fontWeight: 'bold',
    flexShrink: 0,
    height: '16px',
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    width: '40px',
    textAlign: 'center',
  },
  primaryLabel: {
    backgroundColor: '#1A65FF',
    color: '#FFFFFF',
  },
  subLabel: {
    backgroundColor: '#E8E8E8',
    color: '#666666',
  },
  industryLine: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '6px',
  },
  industryText: {
    fontSize: '12px',
    lineHeight: '1.4',
    paddingTop: '1px',
  },
});

class CompanyProfile extends React.Component {
  /**
   * 企業の業種カテゴリ（小分類ID群）から、大分類 > 中分類 > 小分類 のパスを取得する。
   * メイン業種とサブ業種を区別する。グルーピングはせず、1つずつ表示。
   *
   * 戻り値:
   * - { primary: string[], sub: string[] } メインとサブに分類されたパス配列
   */
  getIndustryPathGroups = () => {
    const { item, industryCategoryList } = this.props;

    if (!item.corporation_industry_minor_category_ids || item.corporation_industry_minor_category_ids.length === 0) {
      return { primary: [], sub: [] };
    }

    const primaryId = item.primary_industry_minor_category_id;
    const allIds = item.corporation_industry_minor_category_ids;

    // メイン業種IDとサブ業種IDを分ける
    const primaryIds = primaryId ? [primaryId] : [];
    const subIds = allIds.filter(id => id !== primaryId);

    // IDからパス（大分類 > 中分類 > 小分類）を取得する関数
    const getIndustryPaths = (ids) => {
      if (ids.length === 0) return [];
      const paths = compressSelectionToPaths(industryCategoryList, Set(ids));
      const result = paths
        .map(path => {
          const names = path.map(n => n && n.name).filter(Boolean);
          return names.join(' > ');
        })
        .filter(Boolean);
      // 重複を排除
      return [...new Set(result)];
    };

    return {
      primary: getIndustryPaths(primaryIds),
      sub: getIndustryPaths(subIds),
    };
  };

  render () {
    const { classes, item } = this.props;
    const { primary, sub } = this.getIndustryPathGroups();
    const hasIndustry = primary.length > 0 || sub.length > 0;
    // メイン業種がない場合（ESの再インデックス前）は、ラベルなしで表示
    const showLabels = primary.length > 0;

    // TODO shikiho_site_urlではなく、corporation_details.site_urlから取得するべきESの生成コマンドの修正が必要
    return (
      <React.Fragment>
        <div className={classes.root}>
          <div className={classes.title}>{`${item.company_name}について`}</div>
          <div className={classes.boxWrap}>
            {item.shikiho_site_url && (<div
              className={classNames('clickable')}
              style={{ width: 'unset' }}
              onClick={() => window.open(item.shikiho_site_url, '_blank')}
            >
              <div className={classes.box} style={{ left: '6px', top: '5.5px' }}></div>
              <div className={classes.box} style={{ left: '9.5px', top: '2px' }}></div>
            </div>)}
          </div>
        </div>
        <div className={classes.inline} style={{ marginBottom: '25px'}}>
          <div className={classes.corpLogo}>
            <img src={item.logo_path} alt="logo" className={classes.logoImg} />
          </div>
          <div style={{ flex: 1 }}>
            {hasIndustry && (
              <div style={{ marginBottom: '10px' }}>
                {showLabels ? (
                  <React.Fragment>
                    {primary.map((line, idx) => (
                      <div key={`primary-${idx}`} className={classes.industryLine}>
                        <span className={classNames(classes.industryLabel, classes.primaryLabel)}>メイン</span>
                        <span className={classes.industryText}>{line}</span>
                      </div>
                    ))}
                    {sub.map((line, idx) => (
                      <div key={`sub-${idx}`} className={classes.industryLine}>
                        <span className={classNames(classes.industryLabel, classes.subLabel)}>サブ</span>
                        <span className={classes.industryText}>{line}</span>
                      </div>
                    ))}
                  </React.Fragment>
                ) : (
                  // メイン業種が設定されていない場合はラベルなしで表示
                  <Typography variant="body2" style={{ fontSize: '12px' }}>
                    {sub.map((line, idx) => (
                      <React.Fragment key={`industry-line-${idx}`}>
                        {idx > 0 ? <br /> : null}
                        {line}
                      </React.Fragment>
                    ))}
                  </Typography>
                )}
              </div>
            )}
            {item.shikiho_feature && (
              <React.Fragment>
                <Typography variant="body2" style={{ fontSize: '12px', marginTop: '10px'}}>{item.shikiho_feature}</Typography>
                <div className={classes.warning}>
                  この文章は、公開されている会社HPの情報を参考にした要約です。内容は正確性を保証するものではありません。最新の情報は必ず公式HPをご確認ください。訂正依頼・お問い合わせは右下のチャットまでお願いいたします。
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

CompanyProfile.propTypes = {
  classes: PropTypes.object.isRequired,
  item: PropTypes.object,
  industryCategoryList: PropTypes.array,
}
const mapStateToProps = (state) => {
  return {
    industryCategoryList: state.industryCategory.industryCategoryList,
  };
};


PropTypes.defaultValue = {
  industryCategoryList: [],
}

export default withStyles(styles)(connect(mapStateToProps)(CompanyProfile));



// WEBPACK FOOTER //
// ./src/components/ExecutiveList/dialogs/ExecDetailItemDialog/parts/CompanyProfile.jsx